/**
 * A prova do protocolo de notificação no detalhe da passagem (`OB-08`, ADR-071).
 *
 * Pela Res. ANTT nº 6.079/2026, o poder concedente só divide a perda da evasão se a
 * concessionária provar a notificação digital no 8º e no 15º dia após a passagem. O cartão
 * mostra o dia de cada etapa, a situação (do servidor, pela regra do domínio) e a tentativa
 * que a prova — e, embaixo, todas as tentativas registradas, as que não provam também. Só
 * leitura: quem registra é o sistema de notificação da concessionária, ou a supervisão pela
 * planilha, na tela de Prazos.
 */
import type { DossieDaPassagem } from "../api/tipos";
import { Badge } from "../componentes/base";
import { Icone } from "../componentes/icones";
import { COR_DA_SITUACAO, diaDaEtapa, resumoDaTentativa } from "./protocolo";

export function ProvaDaNotificacao({ dossie }: { dossie: DossieDaPassagem }) {
  return (
    <section
      aria-label="Prova da notificação"
      className="grid content-start gap-2 rounded-cartao border border-linha bg-painel-2 p-2.5"
    >
      <header className="flex flex-wrap items-center gap-1.5">
        <h3 className="flex items-center gap-1.5 text-titulo-cartao font-bold tracking-[0.07em] text-tinta-3 uppercase">
          <Icone.Escudo />
          Prova da notificação
        </h3>
        {dossie.aplica &&
          (dossie.comprovado ? (
            <Badge cor="verde">comprovada</Badge>
          ) : dossie.em_falta ? (
            <Badge cor="vermelho">em falta</Badge>
          ) : (
            <Badge cor="amarelo">em andamento</Badge>
          ))}
        <span className="ml-auto text-[10.5px] text-tinta-3">
          {dossie.regime}
        </span>
      </header>

      {!dossie.aplica ? (
        <p className="text-[11px] text-tinta-3">
          Fora do protocolo: {dossie.fora}
        </p>
      ) : (
        <ul className="grid gap-1">
          {dossie.etapas.map((e) => (
            <li
              key={e.etapa}
              className="grid gap-0.5 border-b border-linha pb-1 last:border-0"
              title={e.descricao}
            >
              <div className="flex flex-wrap items-center gap-1.5 text-[11.5px]">
                <b className="text-tinta-2">{e.rotulo}</b>
                {diaDaEtapa(e) && (
                  <span className="mono text-tinta-3">{diaDaEtapa(e)}</span>
                )}
                <span className="ml-auto">
                  <Badge cor={COR_DA_SITUACAO[e.situacao]}>
                    {e.situacao_rotulo}
                  </Badge>
                </span>
              </div>
              {e.prova && (
                <p className="mono text-[10.5px] text-tinta-2">
                  {resumoDaTentativa(e.prova)}
                </p>
              )}
            </li>
          ))}
        </ul>
      )}

      {dossie.tentativas.length > 0 ? (
        <details>
          <summary className="cursor-pointer text-[11px] text-tinta-3">
            {dossie.tentativas.length === 1
              ? "1 tentativa registrada"
              : `${dossie.tentativas.length} tentativas registradas`}
          </summary>
          <ul className="mt-1 grid gap-0.5 text-[10.5px] text-tinta-2">
            {dossie.tentativas.map((t) => (
              <li
                key={`${t.etapa}-${t.recibo}`}
                className="flex flex-wrap gap-1"
              >
                <span className="text-tinta-3">{t.etapa_rotulo}:</span>
                <span className="mono">{resumoDaTentativa(t)}</span>
                <span>· {t.resultado_rotulo}</span>
                <span className="text-tinta-3">
                  · {t.origem === "API" ? "sistema de notificação" : "planilha"}
                  {t.registrada_por ? ` (${t.registrada_por})` : ""}
                </span>
                {t.prova && <Badge cor="verde">prova</Badge>}
              </li>
            ))}
          </ul>
        </details>
      ) : (
        <p className="text-[11px] text-tinta-3">
          Nenhuma tentativa registrada.
        </p>
      )}
      <p className="text-[10px] leading-snug text-tinta-3">
        {dossie.fonte}. {dossie.guarda}
      </p>
    </section>
  );
}
